// Route Code Edited 7/29/2026
/**
 * Route T&C/Checkbox Handler
 *
 * PROBLEM: Route's native checkout process doesn't validate custom checkboxes
 * (e.g., age verification, terms & conditions) before proceeding to checkout.
 *
 * SOLUTION: Intercepts clicks on Route's checkout button to validate required
 * checkboxes. If validation fails, redirects to the original checkout button
 * instead, preventing checkout until requirements are met.
 *
 * Configuration Selectors:
 * - origCheckout: Original checkout button (excludes Route's cloned button)
 * - routeCheckout: Route's cloned checkout button
 * - routeCWC: Route's "Continue Without Coverage" link
 * - cartContainer: Optional array of cart container selectors to scope search
 *   • Limits checkbox/button search to specific cart areas
 *   • If empty, entire document is searched
 *   • Example: ['#cart-drawer', '.cart-page-checkout-container']
 * - requiredCheckboxes: Array of checkbox selectors that must be checked
 *   • Missing checkboxes are treated as valid (won't block checkout)
 *   • Example: ['#age-verification', '.terms-checkbox input', '[name="agree"]']
 *
 * Installation:
 *   Add this script tag at the bottom of layout/theme.liquid (before </body>):
 *   <!-- Route Code Edited 07/__/2026 -->
 *   <script src="{{ 'route-checkbox-handler.js' | asset_url }}" defer></script>
 *   <!-- End Route Code -->
 *  
 */
const config = {
  selectors: {
    origCheckout: '[data-original-display]:not([data-route-cloned-button])',
    routeCheckout: '[data-route-cloned-button]',
    routeCWC: '.route-continue-without-coverage-link',
    cartContainer: [],
    requiredCheckboxes: ['#cart-warning']
  }
};

const { selectors } = config;

const ROUTE_BUTTON_SELECTOR = `${selectors.routeCheckout}, ${selectors.routeCWC}`;

// Use capture phase to intercept clicks BEFORE Route's event handlers.
document.addEventListener("click", (event) => {
  // Check if the event was on a Route checkout element
  const clickedRouteButton = event.target.closest(ROUTE_BUTTON_SELECTOR);
  if (!clickedRouteButton) return;

  // Determine search scope (nearest cart container or entire document).
  const containerElement = selectors.cartContainer.length
    ? selectors.cartContainer.reduce((found, selector) =>
        found || clickedRouteButton.closest(selector), null)
    : null;
  const searchContext = containerElement ?? document;

  // Check if all required checkboxes are checked.
  // Missing checkboxes are treated as valid — this prevents blocking checkout
  // if an optional checkbox isn't rendered on a particular page.
  const allCheckboxesChecked = selectors.requiredCheckboxes.every(checkboxSelector => {
    const checkbox = searchContext.querySelector(checkboxSelector);
    return checkbox?.checked ?? true;
  });

  // Allow Route's normal behavior if validation passes
  if (allCheckboxesChecked) return;

  // Validation failed - find original checkout button to redirect to
  const allOriginalButtons = searchContext.querySelectorAll(selectors.origCheckout);

  if (allOriginalButtons.length === 0) {
    console.error(`Route checkout redirect failed: No original checkout button found matching selector: ${selectors.origCheckout}`);
    return;
  }
  if (allOriginalButtons.length > 1) {
    console.warn(`Route checkout redirect: Found ${allOriginalButtons.length} original checkout buttons matching selector: ${selectors.origCheckout}. Clicking the first one.`);
  }

  // Block Route's checkout action
  event.preventDefault();
  event.stopImmediatePropagation();

  // Trigger original checkout (will show native validation messages)
  allOriginalButtons[0].click();
}, { capture: true });
// End Route Code